<template>
  <div class="user-search" :class="{ 'user-search--loading': loading }">
    <AutoCompleteInput
      name="user-search"
      :list-name="'user-search-result'"
      :placeholder="$t('product_events.SEARCH_USER_PLACEHOLDER')"
      :value="searchQuery"
      :error-message="null"
      :no-container="true"
      scrollable
      align="right"
      :options="users"
      :data-cy="'add-user-autocomplete'"
      @onKeyUp="searchFieldKeyUpHandler"
      @optionSelected="onSelectByIndex"
    >
      <template #default="{ activeIndex }">
        <AutoCompleteOption
          v-for="(user, index) in users"
          :key="user.id"
          :data-cy="user.id"
          :selected="index === activeIndex"
          @optionClicked="onSelect(user)"
        >
          <template #title>
            <span :title="`${user.first_name} ${user.last_name}`"
              >{{ user.first_name }} {{ user.last_name }}</span
            >
          </template>

          <template #details>
            <span
              v-if="checkStringProperty(user, 'phone_number')"
              :title="user.phone_number"
              >{{ user.phone_number }}</span
            >
            <span
              v-if="checkStringProperty(user, 'email')"
              :title="user.email"
              >{{ user.email }}</span
            >
          </template>
        </AutoCompleteOption>

        <div
          v-if="!loading && users.length === 0"
          class="user-search__state"
          data-cy="event-user-search-empty"
        >
          {{ $t('FILTERS_NO_USERS') }}
        </div>

        <div
          v-if="hasMoreMatches"
          class="user-search__state"
          data-cy="event-user-search-truncated"
        >
          {{ $t('EVENT_USER_SEARCH_MORE_MATCHES') }}
        </div>
      </template>
    </AutoCompleteInput>
  </div>
</template>

<script setup lang="ts">
import AutoCompleteInput from '@/components/generics/AutoCompleteInput.vue'
import AutoCompleteOption from '@/components/generics/AutoCompleteOption.vue'
import { checkStringProperty } from '@/helpers/stringHelpers.js'
import { computed, ref } from 'vue'
import { useAddUserToDateSearch } from './useAddUserToDate.ts'

type UserSearchResult = {
  id: number
  first_name: string
  last_name: string
  phone_number?: string | null
  email?: string | null
}

withDefaults(
  defineProps<{
    isWaitingList?: boolean
    showHeader?: boolean
  }>(),
  {
    isWaitingList: false,
    showHeader: false,
  }
)

const emit = defineEmits<{
  (event: 'onSelect', user: UserSearchResult): void
}>()

const searchQuery = ref('')
const { users, usersCount, loading, searchUsers } = useAddUserToDateSearch()
const hasMoreMatches = computed(
  () => !loading.value && usersCount.value > users.value.length
)

const searchFieldKeyUpHandler = (value: string): void => {
  searchUsers(value)
}

const onSelect = (user: UserSearchResult): void => {
  emit('onSelect', user)
}

const onSelectByIndex = (index: number): void => {
  const user = users.value[index]
  if (user) onSelect(user)
}
</script>

<style lang="scss" scoped>
.user-search {
  position: relative;
  z-index: 2;

  &--loading {
    .option {
      opacity: 0.5;
      pointer-events: none;
      animation: skeletonAnimation 2000ms ease-in-out;
    }

    @keyframes skeletonAnimation {
      0%,
      100% {
        opacity: 0.5;
      }
      75% {
        opacity: 0.7;
      }
    }
  }

  &__state {
    padding: $spacing-3xs $spacing-xs;
    color: $color-text-light;
    font-size: $font-size-s;
  }
}
</style>
